import { useState, useEffect, useCallback, useRef } from 'react';
import { companySettingsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Building2, Upload, Trash2, CheckCircle2 } from 'lucide-react';

const FIELDS = [
  { key: 'name', label: 'Company name', placeholder: 'MVP IT Support Desk', testid: 'company-name-input' },
  { key: 'address', label: 'Address', placeholder: '123 Main St, Calgary AB T2P 1A1', testid: 'company-address-input', textarea: true },
  { key: 'phone', label: 'Phone', placeholder: '+1 403 555 0100', testid: 'company-phone-input' },
  { key: 'email', label: 'Email', placeholder: 'info@mvpit.ca', testid: 'company-email-input' },
  { key: 'website', label: 'Website', placeholder: 'https://mvpit.ca', testid: 'company-website-input' },
];export default function CompanySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [form, setForm] = useState({
    name: '', logo_url: '', address: '', phone: '', email: '', website: '',
  });
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await companySettingsAPI.get();
      setForm({
        name: res.data.name || '',
        logo_url: res.data.logo_url || '',
        address: res.data.address || '',
        phone: res.data.phone || '',
        email: res.data.email || '',
        website: res.data.website || '',
      });
    } catch (err) {
      console.error('Failed to load company settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (savedAt) setSavedAt(null);
  };

  const handleLogoFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (PNG, JPG, SVG).');
      return;
    }
    if (file.size > 800 * 1024) {
      alert('Logo must be under 800KB. Try a smaller PNG/JPG or hosted URL.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setField('logo_url', String(reader.result || ''));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = { ...form };
      // Empty string -> null on backend (clears the field).
      const res = await companySettingsAPI.update(payload);
      setForm({
        name: res.data.name || '',
        logo_url: res.data.logo_url || '',
        address: res.data.address || '',
        phone: res.data.phone || '',
        email: res.data.email || '',
        website: res.data.website || '',
      });
      setSavedAt(new Date());
      setTimeout(() => setSavedAt(null), 3000);
    } catch (err) {
      console.error('Failed to save company settings:', err);
      alert(err.response?.data?.detail || 'Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white border-slate-200">
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-slate-200" data-testid="company-settings-card">
      <CardHeader>
        <CardTitle className="text-slate-800 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-cyan-600" />
          Company Settings
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          These details appear on the branded Work Order PDFs your customers receive.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Logo */}
        <div>
          <Label className="text-slate-700 text-xs">Logo</Label>
          <div className="mt-1 flex items-start gap-4">
            <div
              className="w-24 h-24 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden"
              data-testid="company-logo-preview"
            >
              {form.logo_url ? (
                <img src={form.logo_url} alt="logo" className="max-w-full max-h-full object-contain" />
              ) : (
                <Building2 className="w-8 h-8 text-slate-300" />
              )}
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleLogoFile(e.target.files?.[0])}
                  data-testid="company-logo-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="border-slate-300 rounded-full"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="company-logo-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload image
                </Button>
                {form.logo_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-rose-600 hover:bg-rose-50 rounded-full"
                    onClick={() => setField('logo_url', '')}
                    data-testid="company-logo-clear-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                )}
              </div>
              <Input
                value={form.logo_url || ''}
                onChange={(e) => setField('logo_url', e.target.value)}
                placeholder="…or paste a public image URL (https://…)"
                className="border-slate-200"
                data-testid="company-logo-url-input"
              />
              <p className="text-xs text-slate-400">
                PNG/JPG/SVG, max 800KB. The logo renders top-left of every Work Order PDF.
              </p>
            </div>
          </div>
        </div>

        {/* Text fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.textarea ? 'md:col-span-2' : ''}>
              <Label className="text-slate-700 text-xs">{f.label}</Label>
              {f.textarea ? (
                <Textarea
                  value={form[f.key] || ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  rows={2}
                  className="border-slate-200"
                  data-testid={f.testid}
                />
              ) : (
                <Input
                  value={form[f.key] || ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="border-slate-200"
                  data-testid={f.testid}
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-cyan-500 hover:bg-cyan-600 rounded-full"
            data-testid="company-settings-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save changes
          </Button>
          {savedAt && (
            <span className="text-sm text-emerald-600 inline-flex items-center gap-1" data-testid="company-settings-saved-flag">
              <CheckCircle2 className="w-4 h-4" /> Saved
            </span>
          )}
        </div>

      </CardContent>
    </Card>
  );
}
